import {
  Activity,
  Check,
  ChevronDown,
  ClockSnooze,
  Plus,
  SearchMd,
  ShieldZap,
  Stars02,
  Trophy01,
  Users01,
} from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import type { Key } from "react-aria";
import { useCreatorLists } from "@/api/creator-lists";
import type { BrandCreatorClassification } from "@/api/creator-metrics";
import { Tabs } from "@/components/application/tabs/tabs";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Input } from "@/components/base/input/input";
import { useIsAssurance } from "@/hooks/use-is-assurance";
import { cx } from "@/utils/cx";

type ClassificationTab = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  classification?: BrandCreatorClassification | "safe_creator";
  enabled?: boolean;
};

type Props = {
  selected: string;
  setSelected: (selected: string) => void;
  selectedList?: string;
  setSelectedList?: (list: string) => void;
  showCustomLists?: boolean;
  showRecommendations?: boolean;
};

export const CreatorClassificationTabs = ({
  selected,
  setSelected,
  selectedList,
  setSelectedList,
  showCustomLists = false,
  showRecommendations = false,
}: Props) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const { data: lists } = useCreatorLists({ offset: 0, limit: 50 }, "", { column: "createdAt", direction: "asc" });
  const isAssurance = useIsAssurance();

  const CLASSIFICATION_TABS: ClassificationTab[] = [
    {
      id: "recommended_creators",
      label: "Best Matches",
      icon: <Stars02 className="w-4 h-4" />,
    },
    {
      id: "top_creator",
      label: "Top Performers",
      icon: <Trophy01 className="w-4 h-4" />,
      classification: "top_creator",
    },
    {
      id: "active_partner",
      label: "Active Partners",
      icon: <Activity className="w-4 h-4" />,
      classification: "active_partner",
    },
    {
      id: "dormant_creator",
      label: "Dormant",
      icon: <ClockSnooze className="w-4 h-4" />,
      classification: "dormant_creator",
    },
    {
      id: "safe_creator",
      label: "Safer Creators",
      icon: <ShieldZap className="w-5 h-4" />,
      classification: "safe_creator",
      enabled: isAssurance,
    },
    { id: "__all", label: "All", icon: <Users01 className="w-4 h-4" /> },
  ];

  useEffect(() => setSearch(""), [open]);

  const filteredLists = useMemo(() => {
    if (!lists) return [];
    const list = lists.lists.map((list) => ({ id: list.id, label: list.name }));
    if (search.length) {
      const lowered = search.toLocaleLowerCase();
      return list.filter((item) => item.label.toLocaleLowerCase().includes(lowered));
    }
    return list;
  }, [lists, search]);

  const isListSelected = selectedList && selectedList !== "__all";
  const selectedListName = isListSelected ? lists?.lists.find((l) => l.id === selectedList)?.name : undefined;
  const hasCustomLists = lists && lists.lists.length > 0;
  const showSearch = lists && lists.lists.length > 10;

  if (!lists && showCustomLists) return <div className="h-6 w-56 animate-pulse rounded bg-gray-200" />;

  return (
    <>
      <Tabs selectedKey={selected as Key} onSelectionChange={(key) => setSelected(key as string)}>
        <Tabs.List
          type="underline"
          items={CLASSIFICATION_TABS.filter(
            (t) => t.enabled !== false && (t.id !== "recommended_creators" || showRecommendations),
          )}
        >
          {(tab) => <Tabs.Item {...tab} className="min-w-10 max-w-56" />}
        </Tabs.List>
      </Tabs>
      {showCustomLists && setSelectedList && (
        <Dropdown.Root isOpen={open} onOpenChange={(open) => setOpen(open)}>
          <Button
            iconTrailing={<ChevronDown className="h-4 w-4" />}
            className={cx(
              "p-0 pb-[11px] pr-2 pl-4 text-sm font-semibold bg-transparent text-quaternary shadow-none hover:text-brand-secondary relative before:absolute before:inset-x-0 before:bottom-0 border-b-[1px] border-solid border-secondary rounded-none hover:border-fg-brand-primary_alt delay-100 hover:border-b-2 hover:pb-[10px] transition-all focus:outline-none",
              (open || isListSelected) && "text-brand-secondary border-fg-brand-primary_alt border-b-2 pb-[10px]",
            )}
            color="tertiary"
          >
            {selectedListName ?? "Custom Lists"}
          </Button>
          <Dropdown.Popover>
            <Dropdown.Menu>
              {hasCustomLists && (
                <>
                  <Dropdown.Section className="max-h-96 overflow-auto">
                    {filteredLists.map((item) => (
                      <Dropdown.Item
                        icon={() => (
                          <Check
                            className={cx(
                              item.id !== selectedList && "opacity-0",
                              "mr-2 size-4 shrink-0 stroke-[2.25px]",
                            )}
                          />
                        )}
                        onClick={() => setSelectedList(item.id)}
                        key={item.id}
                      >
                        <span>{item.label}</span>
                      </Dropdown.Item>
                    ))}
                    {!filteredLists.length && (
                      <Dropdown.Item className="flex flex-row justify-center hover:bg-transparent">
                        <span className="text-center font-normal italic">No Results</span>
                      </Dropdown.Item>
                    )}
                  </Dropdown.Section>
                  <Dropdown.Separator />
                  <Dropdown.Section>
                    {showSearch && (
                      <Dropdown.Item>
                        <Input
                          icon={SearchMd}
                          placeholder="Search"
                          value={search}
                          onChange={setSearch}
                          onClick={(e) => e.stopPropagation()}
                          size="sm"
                          inputClassName="text-sm"
                        />
                      </Dropdown.Item>
                    )}
                    <Dropdown.Item onClick={() => setSelectedList("__create-new")} icon={Plus}>
                      Create new list
                    </Dropdown.Item>
                  </Dropdown.Section>
                </>
              )}
              {!hasCustomLists && (
                <Dropdown.Section>
                  <Dropdown.Item onClick={() => setSelectedList("__create-new")} icon={Plus}>
                    Create new list
                  </Dropdown.Item>
                </Dropdown.Section>
              )}
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown.Root>
      )}
      <div className="pl-4 bg-transparent border-b-[1px] border-solid border-secondary rounded-none w-full" />
    </>
  );
};

export const getClassificationFromTab = (tabId: string): BrandCreatorClassification | undefined => {
  if (!["top_creator", "rising_star", "active_partner", "dormant_creator", "safe_creator"].includes(tabId)) return;
  return tabId as BrandCreatorClassification;
};
